/**
 * Luxury Variant Picker JavaScript
 * Handles variant selection and updates for the luxury variant picker blocks
 */

class LuxuryVariantPicker extends HTMLElement {
  constructor() {
    super();
    this.pendingAPICall = null; // For debouncing variant API calls
    this.pendingSwatchFetch = null; // For debouncing swatch filter API calls
  }

  connectedCallback() {
    // Get variant data from the script tag
    const scriptElement = this.querySelector('script[type="application/json"]');
    if (!scriptElement) {
      console.error('Luxury Variant Picker: No variant data found');
      return;
    }

    try {
      this.variantData = JSON.parse(scriptElement.textContent || '[]');
    } catch (e) {
      console.error('Luxury Variant Picker: Failed to parse variant data', e);
      return;
    }

    // Find the current variant
    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput) {
      this.currentVariant = this.variantData.find(variant => variant.id == variantInput.value);
    }

    // Initialize event listeners for all input types
    this.initializeEventListeners();

    // Set initial selected display
    this.updateSelectedDisplay();

    console.log('Luxury Variant Picker initialized with', this.variantData.length, 'variants');
  }

  initializeEventListeners() {
    // Handle radio inputs (swatches, boxes, buttons, tiles)
    const radioInputs = this.querySelectorAll('input[type="radio"]');
    radioInputs.forEach(input => {
      input.addEventListener('change', this.handleVariantChange.bind(this));
    });

    // Handle select dropdowns
    const selectInputs = this.querySelectorAll('select');
    selectInputs.forEach(select => {
      select.addEventListener('change', this.handleVariantChange.bind(this));
    });

    // Initialize grouped swatches functionality
    this.initializeGroupedSwatches();

    // Initialize virtual colors functionality
    this.initializeVirtualColors();

    // Add click handlers for label elements to ensure proper selection
    const labels = this.querySelectorAll('label.swatch, label.box, label.variant-button, label.tile, label.radio-option');
    labels.forEach(label => {
      // Make label clickable
      label.style.cursor = 'pointer';

      label.addEventListener('click', (e) => {
        // Find the input inside this label
        const input = label.querySelector('input[type="radio"]');
        if (input && !input.disabled) {
          // First, uncheck all radios in the same group
          const groupName = input.getAttribute('name');
          const allInGroup = this.querySelectorAll(`input[name="${groupName}"]`);
          allInGroup.forEach(radio => {
            radio.checked = false;
            // Remove selected classes from parent
            const parent = radio.closest('.swatch, .box, .variant-button, .tile');
            if (parent) {
              parent.classList.remove('swatch--selected', 'box--selected', 'variant-button--selected', 'tile--selected');
            }
          });

          // Now check this one
          input.checked = true;

          // Add selected class to parent
          const parent = input.closest('.swatch, .box, .variant-button, .tile');
          if (parent) {
            if (parent.classList.contains('swatch')) parent.classList.add('swatch--selected');
            else if (parent.classList.contains('box')) parent.classList.add('box--selected');
            else if (parent.classList.contains('variant-button')) parent.classList.add('variant-button--selected');
            else if (parent.classList.contains('tile')) parent.classList.add('tile--selected');
          }

          // Trigger change event
          input.dispatchEvent(new Event('change', { bubbles: true }));

          // Prevent the default label behavior to avoid double-firing
          e.preventDefault();
        }
      });
    });
  }

  handleVariantChange(event) {
    // Always update the selected display text first
    this.updateSelectedDisplay();

    const selectedOptions = this.getSelectedOptions();

    // First try to find variant in local data (works for products with <=250 variants)
    let variant = this.getVariantFromOptions(selectedOptions);

    if (variant) {
      this.currentVariant = variant;
      this.updateVariant(variant);
    } else {
      // Variant not found in local data - could be beyond 250 limit
      // Use Section Rendering API to fetch the correct variant
      this.fetchVariantViaAPI(selectedOptions);
    }
  }

  async fetchVariantViaAPI(selectedOptions) {
    // Debounce API calls - cancel any pending call
    if (this.pendingAPICall) {
      clearTimeout(this.pendingAPICall);
    }

    // Get product handle and section ID from the grouped swatches container or data attributes
    const groupedContainer = this.querySelector('[data-grouped-swatches]');
    const productHandle = groupedContainer?.dataset.productHandle || this.dataset.url?.split('/products/')[1];
    const sectionId = groupedContainer?.dataset.sectionId || this.dataset.section;

    if (!productHandle) {
      this.handleUnavailableVariant(selectedOptions);
      return;
    }

    // Build URL with option_values parameter for Section Rendering API
    // Using the format: option_values=option1_value,option2_value,option3_value
    // This is Shopify's recommended approach for high-variant products (>250 variants)
    const optionValuesParam = selectedOptions.map(val => encodeURIComponent(val)).join(',');
    const url = `/products/${productHandle}?option_values=${optionValuesParam}&sections=${sectionId}`;

    // Debounce: wait 50ms before making the API call
    this.pendingAPICall = setTimeout(async () => {
      console.log('Fetching variant via Section API:', url);
      await this._executeAPICall(url, sectionId, selectedOptions);
    }, 50);
  }

  async _executeAPICall(url, sectionId, selectedOptions) {

    try {
      const response = await fetch(url);
      console.log('Section API response status:', response.status);

      if (!response.ok) {
        console.error('Section API returned error:', response.status);
        this.handleUnavailableVariant(selectedOptions);
        return;
      }

      const data = await response.json();
      console.log('Section API returned sections:', Object.keys(data));

      const html = data[sectionId];

      if (html) {
        // Parse the returned HTML to extract variant data
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Find the variant input in the returned section
        const variantInput = doc.querySelector('input[name="id"]');
        console.log('Found variant input in response:', variantInput ? variantInput.value : 'NOT FOUND');

        if (variantInput && variantInput.value) {
          const variantId = parseInt(variantInput.value);
          console.log('Variant ID from Section API:', variantId);

          // Update our variant input
          const localInput = this.querySelector('input[name="id"]');
          if (localInput) {
            localInput.value = variantId;
          }

          // Update URL
          const pageUrl = new URL(window.location);
          pageUrl.searchParams.set('variant', variantId);
          window.history.replaceState({}, '', pageUrl);

          // Dispatch variant change event
          this.dispatchEvent(new CustomEvent('variant-change', {
            detail: { variantId: variantId },
            bubbles: true
          }));

          console.log('Variant updated successfully via Section API');
          return;
        }
      } else {
        console.error('No HTML returned for section:', sectionId);
      }

      this.handleUnavailableVariant(selectedOptions);
    } catch (e) {
      console.error('Failed to fetch variant via API:', e);
      this.handleUnavailableVariant(selectedOptions);
    }
  }

  getSelectedOptions() {
    const options = [];

    // Get all variant options
    const optionElements = this.querySelectorAll('.variant-option');
    console.log('Found', optionElements.length, 'variant option groups');

    optionElements.forEach((optionElement, index) => {
      let selectedValue = null;

      // Check radio inputs
      const checkedRadio = optionElement.querySelector('input[type="radio"]:checked');
      if (checkedRadio) {
        selectedValue = checkedRadio.value;
      }

      // Check select dropdown
      const selectElement = optionElement.querySelector('select');
      if (selectElement) {
        selectedValue = selectElement.value;
      }

      if (selectedValue) {
        options.push(selectedValue);
        console.log(`Option ${index}: ${selectedValue}`);
      }
    });

    return options;
  }

  getVariantFromOptions(options) {
    return this.variantData.find(variant => {
      return options.every((option, index) => {
        return variant.options[index] === option;
      });
    });
  }

  updateVariant(variant) {
    // Update hidden variant ID input
    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = variant.id;
    }

    // Update selected value display
    this.updateSelectedDisplay();

    // Update availability
    this.updateAvailability(variant);

    // Dispatch custom event for other components to listen to
    this.dispatchEvent(new CustomEvent('variant-change', {
      detail: { variant: variant },
      bubbles: true
    }));

    // Update URL with variant parameter
    this.updateURL(variant);

    // Update product form if exists
    this.updateProductForm(variant);

    // Update price if element exists
    this.updatePrice(variant);

    // Update product image if main gallery exists
    this.updateProductImage(variant);
  }

  /**
   * @param {HTMLInputElement | HTMLOptionElement | null} input
   * @returns {string}
   */
  getOptionDisplayLabel(input) {
    if (!input) return '';

    if (input instanceof HTMLOptionElement) {
      return input.textContent?.trim() || input.value;
    }

    const container = input.closest('.swatch, .box, .variant-button, .tile, .radio-option');
    if (container) {
      const labelEl = container.querySelector(
        '.variant-button__label, .box__label, .tile__label, .radio-option__label, .swatch__tooltip'
      );
      if (labelEl?.textContent?.trim()) {
        return labelEl.textContent.trim();
      }
    }

    return input.value;
  }

  updateSelectedDisplay() {
    // Update the selected value text for each option
    this.querySelectorAll('.variant-option').forEach(optionElement => {
      const selectedSpan = optionElement.querySelector('.variant-option__selected');

      // Remove all selected classes first
      optionElement.querySelectorAll('.swatch, .box, .variant-button, .tile').forEach(el => {
        el.classList.remove('swatch--selected', 'box--selected', 'variant-button--selected', 'tile--selected');
      });

      let selectedValue = null;

      const select = optionElement.querySelector('select');
      if (select) {
        selectedValue = this.getOptionDisplayLabel(select.options[select.selectedIndex]);
      } else {
        const checkedRadio = optionElement.querySelector('input[type="radio"]:checked');
        if (checkedRadio) {
          selectedValue = this.getOptionDisplayLabel(checkedRadio);

          // Add selected class to the parent element
          const parentLabel = checkedRadio.closest('.swatch, .box, .variant-button, .tile');
          if (parentLabel) {
            if (parentLabel.classList.contains('swatch')) {
              parentLabel.classList.add('swatch--selected');
            } else if (parentLabel.classList.contains('box')) {
              parentLabel.classList.add('box--selected');
            } else if (parentLabel.classList.contains('variant-button')) {
              parentLabel.classList.add('variant-button--selected');
            } else if (parentLabel.classList.contains('tile')) {
              parentLabel.classList.add('tile--selected');
            }
          }
        }
      }

      if (selectedValue && selectedSpan) {
        selectedSpan.textContent = selectedValue;
      }
    });
  }

  updateAvailability(variant) {
    // Look for availability element in parent container
    const availabilityElement = this.closest('.luxury-variant-picker').querySelector('.variant-availability');
    if (!availabilityElement) return;

    if (variant.available) {
      availabilityElement.innerHTML = `
        <span class="availability availability--in-stock">
          <span class="availability__icon">✓</span>
          In Stock
        </span>
      `;
    } else {
      availabilityElement.innerHTML = `
        <span class="availability availability--out-of-stock">
          <span class="availability__icon">✗</span>
          Out of Stock
        </span>
      `;
    }
  }

  updateURL(variant) {
    if (!variant || !variant.id) return;

    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  updateProductForm(variant) {
    // Update any product form on the page
    const productForm = document.querySelector('product-form');
    if (productForm) {
      const input = productForm.querySelector('input[name="id"]');
      if (input) {
        input.value = variant.id;
        // Trigger form update
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Update add to cart button
    const addToCartButton = document.querySelector('[name="add"]');
    if (addToCartButton) {
      if (variant.available) {
        addToCartButton.disabled = false;
        addToCartButton.textContent = addToCartButton.dataset.addToCart || 'Add to Cart';
      } else {
        addToCartButton.disabled = true;
        addToCartButton.textContent = addToCartButton.dataset.soldOut || 'Sold Out';
      }
    }
  }

  updatePrice(variant) {
    // Find price elements and update them
    const priceElements = document.querySelectorAll('.price__regular .price-item, .price');
    priceElements.forEach(element => {
      if (variant.price && element.dataset.productPrice !== false) {
        // Format price (assuming price is in cents)
        const formattedPrice = this.formatMoney(variant.price);
        element.textContent = formattedPrice;
      }
    });

    // Handle compare at price
    if (variant.compare_at_price > variant.price) {
      const compareElements = document.querySelectorAll('.price__sale .price-item--regular');
      compareElements.forEach(element => {
        element.textContent = this.formatMoney(variant.compare_at_price);
      });
    }
  }

  updateProductImage(variant) {
    if (!variant.featured_image) return;

    // Update main product image
    const mainImage = document.querySelector('.product__media img');
    if (mainImage) {
      mainImage.src = variant.featured_image.src;
      mainImage.srcset = variant.featured_image.srcset || '';
    }

    // Update slideshow if exists
    const slideshow = document.querySelector('slideshow-component');
    if (slideshow && variant.featured_image) {
      // Find slide with matching image
      const slides = slideshow.querySelectorAll('.slideshow__slide');
      slides.forEach((slide, index) => {
        const img = slide.querySelector('img');
        if (img && img.src.includes(variant.featured_image.id)) {
          // Activate this slide
          slideshow.goToSlide && slideshow.goToSlide(index);
        }
      });
    }
  }

  formatMoney(cents) {
    // Simple money formatting - enhance based on your locale needs
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  handleUnavailableVariant(selectedOptions) {
    // Handle case when selected combination doesn't exist
    console.log('Selected variant combination not available:', selectedOptions);

    const availabilityElement = this.querySelector('.variant-availability');
    if (availabilityElement) {
      availabilityElement.innerHTML = `
        <span class="availability availability--unavailable">
          <span class="availability__icon">✗</span>
          Unavailable
        </span>
      `;
    }
  }

  initializeGroupedSwatches() {
    // Find grouped swatch containers
    const groupedContainers = this.querySelectorAll('[data-grouped-swatches]');
    if (groupedContainers.length === 0) return;

    groupedContainers.forEach(container => {
      const parentOptionName = container.dataset.parentOption;

      if (!parentOptionName) return;

      // Find the parent option element
      const parentOptionElement = this.querySelector(`[data-option-name="${parentOptionName}"]`);
      if (!parentOptionElement) return;

      // Listen for changes on the parent option
      // Only use the change event - it fires when the radio value actually changes
      // Avoid duplicate listeners (like click on labels) that would cause double API calls
      const parentInputs = parentOptionElement.querySelectorAll('input[type="radio"], select');
      parentInputs.forEach(input => {
        input.addEventListener('change', () => {
          this.handleParentChange(container, input.value);
        });
      });
    });
  }

  handleParentChange(container, parentValue) {
    // Show the correct group and select first child
    this.updateGroupedSwatchesFallback(container, parentValue);
  }

  reattachSwatchListeners(container) {
    // Re-attach click handlers to newly added swatch labels
    const labels = container.querySelectorAll('label.swatch, label.box, label.variant-button');
    labels.forEach(label => {
      label.style.cursor = 'pointer';

      // Remove existing listeners by cloning
      const newLabel = label.cloneNode(true);
      label.parentNode.replaceChild(newLabel, label);

      newLabel.addEventListener('click', (e) => {
        const input = newLabel.querySelector('input[type="radio"]');
        if (input && !input.disabled) {
          const groupName = input.getAttribute('name');
          const allInGroup = this.querySelectorAll(`input[name="${groupName}"]`);
          allInGroup.forEach(radio => {
            radio.checked = false;
            const parent = radio.closest('.swatch, .box, .variant-button, .tile');
            if (parent) {
              parent.classList.remove('swatch--selected', 'box--selected', 'variant-button--selected', 'tile--selected');
            }
          });

          input.checked = true;

          const parent = input.closest('.swatch, .box, .variant-button, .tile');
          if (parent) {
            if (parent.classList.contains('swatch')) parent.classList.add('swatch--selected');
            else if (parent.classList.contains('box')) parent.classList.add('box--selected');
            else if (parent.classList.contains('variant-button')) parent.classList.add('variant-button--selected');
            else if (parent.classList.contains('tile')) parent.classList.add('tile--selected');
          }

          input.dispatchEvent(new Event('change', { bubbles: true }));
          e.preventDefault();
        }
      });
    });

    // Re-attach change listeners to radio inputs
    const radioInputs = container.querySelectorAll('input[type="radio"]');
    radioInputs.forEach(input => {
      input.addEventListener('change', this.handleVariantChange.bind(this));
    });
  }

  updateGroupedSwatchesFallback(container, parentValue) {
    // Hide all groups
    const allGroups = container.querySelectorAll('.grouped-swatches__group');
    allGroups.forEach(group => {
      group.classList.remove('grouped-swatches__group--active');
    });

    // Show the group matching the selected parent
    const activeGroup = container.querySelector(`[data-parent-value="${parentValue}"]`);
    if (activeGroup) {
      activeGroup.classList.add('grouped-swatches__group--active');

      // Check if there's a selected child in this group
      const selectedChild = activeGroup.querySelector('input[type="radio"]:checked');
      if (!selectedChild) {
        // Auto-select the first child in this group
        const firstChild = activeGroup.querySelector('input[type="radio"]');
        if (firstChild) {
          firstChild.checked = true;
          const parentLabel = firstChild.closest('.swatch, .box, .variant-button');
          if (parentLabel) {
            // Remove selected from all siblings first
            activeGroup.querySelectorAll('.swatch, .box, .variant-button').forEach(el => {
              el.classList.remove('swatch--selected', 'box--selected', 'variant-button--selected');
            });
            // Add selected to this one
            if (parentLabel.classList.contains('swatch')) {
              parentLabel.classList.add('swatch--selected');
            }
          }
          // Trigger change to update variant
          firstChild.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    // Update the selected display text
    this.updateSelectedDisplay();
  }

  // ============================================
  // VIRTUAL COLORS (Not a Shopify Variant)
  // ============================================
  // Virtual colors are displayed as a 4th selector but don't affect the variant ID.
  // Color selection is saved as a cart line item property.

  initializeVirtualColors() {
    // Find virtual colors container
    const virtualContainer = this.querySelector('[data-virtual-colors]');
    if (!virtualContainer) return;

    console.log('Initializing virtual colors');

    const parentOptionName = virtualContainer.dataset.parentOption;

    // Set up click handlers for virtual color swatches
    this.setupVirtualColorHandlers(virtualContainer);

    // Listen for parent option changes to filter virtual colors
    if (parentOptionName) {
      const parentOptionElement = this.querySelector(`[data-option-name="${parentOptionName}"]`);
      if (parentOptionElement) {
        // Get the currently selected parent value
        const checkedInput = /** @type {HTMLInputElement|null} */ (parentOptionElement.querySelector('input[type="radio"]:checked'));
        const selectElement = /** @type {HTMLSelectElement|null} */ (parentOptionElement.querySelector('select'));
        const currentParentValue = (checkedInput && checkedInput.value) ? checkedInput.value : (selectElement ? selectElement.value : null);

        // Check initial state - hide if no colors for current parent
        if (currentParentValue) {
          this.handleParentChangeForVirtualColors(virtualContainer, currentParentValue);
        }

        // Listen for changes
        const parentInputs = parentOptionElement.querySelectorAll('input[type="radio"], select');
        parentInputs.forEach(input => {
          input.addEventListener('change', () => {
            this.handleParentChangeForVirtualColors(virtualContainer, input.value);
          });
        });
      }
    }

    // Set initial selected color display and hidden input
    this.updateVirtualColorDisplay(virtualContainer);
  }

  setupVirtualColorHandlers(container) {
    const colorLabels = container.querySelectorAll('label.swatch--virtual');

    colorLabels.forEach(label => {
      label.style.cursor = 'pointer';

      label.addEventListener('click', (e) => {
        e.preventDefault();

        const input = label.querySelector('input[type="radio"]');
        if (!input) return;

        // Uncheck all virtual color radios
        container.querySelectorAll('input[name="virtual-color"]').forEach(radio => {
          radio.checked = false;
          const parentLabel = radio.closest('.swatch--virtual');
          if (parentLabel) {
            parentLabel.classList.remove('swatch--selected');
          }
        });

        // Check this one
        input.checked = true;
        label.classList.add('swatch--selected');

        // Update the hidden input for cart line item property
        this.updateVirtualColorInput(input.value);

        // Update the selected color display text
        this.updateVirtualColorDisplay(container);

        console.log('Virtual color selected:', input.value);
      });
    });
  }

  updateVirtualColorInput(colorValue) {
    // Update the hidden input that will be submitted with the cart form
    const hiddenInput = this.querySelector('#virtual-color-input');
    if (hiddenInput) {
      hiddenInput.value = colorValue;
      console.log('Updated virtual color input:', colorValue);
    }
  }

  updateVirtualColorDisplay(container) {
    // Find the selected virtual color
    const selectedRadio = container.querySelector('input[name="virtual-color"]:checked');
    const selectedValue = selectedRadio ? this.getOptionDisplayLabel(selectedRadio) : '';

    // Update the label display text
    const displaySpan = this.querySelector('[data-virtual-color-selected]');
    if (displaySpan) {
      displaySpan.textContent = selectedValue;
    }

    // Update the hidden input
    if (selectedValue) {
      this.updateVirtualColorInput(selectedValue);
    }
  }

  handleParentChangeForVirtualColors(container, parentValue) {
    console.log('Parent changed for virtual colors:', parentValue);

    // Find the entire virtual colors option section (the parent of the container)
    const virtualColorsSection = container.closest('.variant-option--virtual-colors');

    // Hide all virtual color groups
    const allGroups = container.querySelectorAll('.virtual-colors__group');
    allGroups.forEach(group => {
      group.classList.remove('virtual-colors__group--active');
    });

    // Show the group matching the selected parent (Upholstery)
    const activeGroup = container.querySelector(`.virtual-colors__group[data-parent-value="${parentValue}"]`);

    // Check if this parent value has any colors defined
    const hasColorsForParent = activeGroup && activeGroup.querySelectorAll('input[name="virtual-color"]').length > 0;

    if (virtualColorsSection) {
      if (hasColorsForParent) {
        // Show the entire virtual colors section
        virtualColorsSection.style.display = '';
        virtualColorsSection.classList.remove('variant-option--hidden');
      } else {
        // Hide the entire virtual colors section (including label)
        virtualColorsSection.style.display = 'none';
        virtualColorsSection.classList.add('variant-option--hidden');

        // Clear the hidden input since no color is selected
        this.updateVirtualColorInput('');

        // Clear the selected color display text
        const displaySpan = this.querySelector('[data-virtual-color-selected]');
        if (displaySpan) {
          displaySpan.textContent = '';
        }

        // Uncheck all virtual color radios
        container.querySelectorAll('input[name="virtual-color"]').forEach(radio => {
          radio.checked = false;
          const parentLabel = radio.closest('.swatch--virtual');
          if (parentLabel) {
            parentLabel.classList.remove('swatch--selected');
          }
        });

        console.log('No colors defined for parent value:', parentValue);
        return;
      }
    }

    if (activeGroup) {
      activeGroup.classList.add('virtual-colors__group--active');

      // Check if there's a selected color in this group
      const selectedColor = activeGroup.querySelector('input[name="virtual-color"]:checked');
      if (!selectedColor) {
        // Auto-select the first color in this group
        const firstColor = activeGroup.querySelector('input[name="virtual-color"]');
        if (firstColor) {
          // Uncheck all colors first
          container.querySelectorAll('input[name="virtual-color"]').forEach(radio => {
            radio.checked = false;
            const parentLabel = radio.closest('.swatch--virtual');
            if (parentLabel) {
              parentLabel.classList.remove('swatch--selected');
            }
          });

          // Select the first color
          firstColor.checked = true;
          const parentLabel = firstColor.closest('.swatch--virtual');
          if (parentLabel) {
            parentLabel.classList.add('swatch--selected');
          }

          // Update the hidden input and display
          this.updateVirtualColorInput(firstColor.value);
        }
      }

      // Update display
      this.updateVirtualColorDisplay(container);
    }
  }
}

// Define custom element
if (!customElements.get('luxury-variant-selector')) {
  customElements.define('luxury-variant-selector', LuxuryVariantPicker);
}

// Handle dynamic content (theme editor, etc.)
document.addEventListener('shopify:section:load', () => {
  // Re-initialize any luxury variant selectors
  document.querySelectorAll('luxury-variant-selector').forEach(selector => {
    if (!selector.initialized) {
      selector.connectedCallback && selector.connectedCallback();
      selector.initialized = true;
    }
  });
});